/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2020 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2020 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { createSelector } from '@reduxjs/toolkit';
import { center as turf_center } from '@turf/turf';
import { dateUtils } from '@opengeoweb/shared';
import type { ReduxLayer } from '../map/layer/types';
import { getFeatureSelectionKey } from '../map/layer/utils';
import type { CoreAppStore } from '../types';
import { selectorMemoizationOptions } from '../utils';
import { SYNCGROUPS_TYPE_SETTIME } from './syncGroups/constants';
import {
  getFeatureLinksForAllPanelsFromState,
  getLinkedFeatureSourcePanelIdsForMapId,
  getLinkedTargetIdsForFeatureSourcePanel,
} from './syncGroups/featureLinks';
import { getFeatureSyncLayersForPanel } from './syncGroups/featureSyncLayers';
import type {
  FeatureLayerData,
  FormFeatureData,
  PotentialData,
  PotentialDataProperties,
  SynchronizationGroupState,
  SynchronizationSource,
} from './syncGroups/types';
import type { SetTimePayload } from './types';
import type { TimeSliderStartEndStepCurrent } from '../map/types';

const emptyPotentialData: PotentialData = {};
const emptyFeatureLayers: FeatureLayerData[] = [];
const emptyLayerIds: string[] = [];
const emptyFeatureIds: string[] = [];

const toFeatureLayerData = (
  layerId: string,
  layer?: ReduxLayer,
): FeatureLayerData | undefined => {
  if (!layer?.geojson) {
    return undefined;
  }

  return {
    layerId,
    originalId: layer.originalId,
    geoJSON: layer.geojson as GeoJSON.FeatureCollection<
      GeoJSON.Geometry,
      PotentialDataProperties | null
    >,
  };
};

const resolveSelectedFeatureFromLayerSelection = (
  layersState: CoreAppStore['layers'] | undefined,
  panelId: string,
):
  | GeoJSON.Feature<GeoJSON.Geometry, PotentialDataProperties | null>
  | undefined => {
  if (!layersState) {
    return undefined;
  }

  const ownedLayerIds = getFeatureSyncLayersForPanel(layersState, panelId).map(
    ({ layerId }) => layerId,
  );

  if (!ownedLayerIds.length) {
    return undefined;
  }

  const selectedFeatures = new Map<
    string,
    GeoJSON.Feature<GeoJSON.Geometry, PotentialDataProperties | null>
  >();

  for (const layerId of ownedLayerIds) {
    const layer = layersState.byId[layerId];
    const selectedFeatureIds = layer?.selectedFeatureIds ?? [];
    if (selectedFeatureIds.length) {
      const selectedFeatureIdSet = new Set(selectedFeatureIds);
      (layer?.geojson?.features ?? []).forEach((feature) => {
        const typedFeature = feature as GeoJSON.Feature<
          GeoJSON.Geometry,
          PotentialDataProperties | null
        >;
        const featureKey = getFeatureSelectionKey(typedFeature);

        if (!featureKey || !selectedFeatureIdSet.has(featureKey)) {
          return;
        }

        selectedFeatures.set(featureKey, typedFeature);
      });
    }
  }

  if (!selectedFeatures.size) {
    return undefined;
  }

  if (selectedFeatures.size === 1) {
    return [...selectedFeatures.values()][0];
  }

  return undefined;
};
const resolveFeatureIdsFromOwnedLayers = (
  layersState: CoreAppStore['layers'] | undefined,
  panelId: string,
  field: 'selectedFeatureIds' | 'hoveredFeatureIds',
): string[] => {
  if (!layersState) {
    return emptyFeatureIds;
  }

  const featureIds = new Set<string>();
  getFeatureSyncLayersForPanel(layersState, panelId).forEach(({ layerId }) => {
    const layerFeatureIds = layersState.byId[layerId]?.[field] ?? [];
    layerFeatureIds.forEach((featureId) => {
      if (featureId) {
        featureIds.add(featureId);
      }
    });
  });

  return featureIds.size > 0 ? [...featureIds] : emptyFeatureIds;
};

const getFeatureSyncStateByPanelId = (
  syncState: SynchronizationGroupState | undefined,
  panelId: string,
): PotentialData | undefined =>
  syncState?.sources?.byId?.[panelId]?.featureSyncState;

const synchronizationGroupStore = (
  store: CoreAppStore,
): SynchronizationGroupState => (store?.syncGroups ? store.syncGroups : null!);

export const getSynchronizationGroupStore = (
  store: CoreAppStore,
): SynchronizationGroupState => {
  return store?.syncGroups ? store.syncGroups : null!;
};

/**
 * Returns the synchronization source by id
 * @param store The app  store
 * @param id The source id to find
 */
const getSyncSourceBySourceId = (
  state: SynchronizationGroupState,
  id: string,
): SynchronizationSource => {
  if (state?.sources) {
    const { sources } = state;
    if (sources.byId[id]) {
      return sources.byId[id];
    }
  }
  return null!;
};

export const getTime = createSelector(
  getSyncSourceBySourceId,
  (store) =>
    store?.payloadByType?.[SYNCGROUPS_TYPE_SETTIME]
      ? (store.payloadByType[SYNCGROUPS_TYPE_SETTIME] as SetTimePayload).value
      : null,
  selectorMemoizationOptions,
);

export const selectFeatureLinksForAllPanels = createSelector(
  synchronizationGroupStore,
  (syncState): Record<string, string[]> =>
    getFeatureLinksForAllPanelsFromState(syncState),
);

export const getLinkedTargetIds = createSelector(
  [
    synchronizationGroupStore,
    (store, panelId?: string): string | undefined => panelId,
  ],
  (syncState, panelId): string[] =>
    getLinkedTargetIdsForFeatureSourcePanel(syncState, panelId),
  selectorMemoizationOptions,
);

export const getLinkedMapIds = createSelector(
  [
    getLinkedTargetIds,
    (store: CoreAppStore): string[] => store?.webmap?.allIds || [],
  ],
  (linkedTargets, mapIds): string[] =>
    linkedTargets.filter((targetId) => mapIds.includes(targetId)),
  selectorMemoizationOptions,
);

export const selectSharedData = createSelector(
  [synchronizationGroupStore, (store, panelId: string): string => panelId],
  (syncState, panelId): PotentialData => {
    const featureSyncStateForPanel = getFeatureSyncStateByPanelId(
      syncState,
      panelId,
    );

    return featureSyncStateForPanel || emptyPotentialData;
  },
  selectorMemoizationOptions,
);

export const selectLinkedPanelIdsForMap = createSelector(
  [selectFeatureLinksForAllPanels, (_, mapViewId: string): string => mapViewId],
  (linksByPanelId, mapViewId): string[] =>
    getLinkedFeatureSourcePanelIdsForMapId(linksByPanelId, mapViewId),
);

export const selectFeatureSyncLayersForPanel = createSelector(
  [
    (store: CoreAppStore): CoreAppStore['layers'] => store.layers,
    (_store: CoreAppStore, panelId: string): string => panelId,
  ],
  (layersState, panelId): FeatureLayerData[] => {
    if (!layersState) {
      return emptyFeatureLayers;
    }

    return getFeatureSyncLayersForPanel(layersState, panelId)
      .map(({ layerId }) => {
        const layer = layersState.byId[layerId];
        return toFeatureLayerData(layerId, layer);
      })
      .filter((layer): layer is FeatureLayerData => !!layer);
  },
  selectorMemoizationOptions,
);

export const selectFeatureSyncLayerIdsForPanel = createSelector(
  [
    (store: CoreAppStore): CoreAppStore['layers'] => store.layers,
    (_store: CoreAppStore, panelId: string): string => panelId,
  ],
  (layersState, panelId): string[] => {
    if (!layersState) {
      return emptyLayerIds;
    }

    return getFeatureSyncLayersForPanel(layersState, panelId).map(
      ({ layerId }) => layerId,
    );
  },
  selectorMemoizationOptions,
);

export const selectSelectedFeatureIdsForPanel = createSelector(
  [
    (store: CoreAppStore): CoreAppStore['layers'] => store.layers,
    (_store: CoreAppStore, panelId: string): string => panelId,
  ],
  (layersState, panelId): string[] =>
    resolveFeatureIdsFromOwnedLayers(
      layersState,
      panelId,
      'selectedFeatureIds',
    ),
  selectorMemoizationOptions,
);

export const selectHoveredFeatureIdsForPanel = createSelector(
  [
    (store: CoreAppStore): CoreAppStore['layers'] => store.layers,
    (_store: CoreAppStore, panelId: string): string => panelId,
  ],
  (layersState, panelId): string[] =>
    resolveFeatureIdsFromOwnedLayers(layersState, panelId, 'hoveredFeatureIds'),
  selectorMemoizationOptions,
);

export const selectHoveredFeatureIdsForMap = createSelector(
  [
    selectFeatureLinksForAllPanels,
    (store: CoreAppStore): CoreAppStore['layers'] => store.layers,
    (_store: CoreAppStore, mapId: string): string => mapId,
  ],
  (linksByPanelId, layersState, mapId): string[] => {
    if (!layersState) {
      return emptyFeatureIds;
    }

    const hoveredFeatureIds = new Set<string>();
    const linkedPanels = getLinkedFeatureSourcePanelIdsForMapId(
      linksByPanelId,
      mapId,
    );

    linkedPanels.forEach((panelId) => {
      getFeatureSyncLayersForPanel(layersState, panelId)
        .filter(({ mapId: layerMapId }) => layerMapId === mapId)
        .forEach(({ layerId }) => {
          const layerHoveredFeatureIds =
            layersState.byId[layerId]?.hoveredFeatureIds ?? [];
          layerHoveredFeatureIds.forEach((featureId) => {
            if (featureId) {
              hoveredFeatureIds.add(featureId);
            }
          });
        });
    });

    return hoveredFeatureIds.size > 0
      ? [...hoveredFeatureIds]
      : emptyFeatureIds;
  },
  selectorMemoizationOptions,
);
export const getSelectedFeature = createSelector(
  [
    (store: CoreAppStore): CoreAppStore['layers'] => store.layers,
    (_store: CoreAppStore, panelId: string): string => panelId,
  ],
  (layersState, panelId) => {
    const selectedFeature = resolveSelectedFeatureFromLayerSelection(
      layersState,
      panelId,
    );

    if (!selectedFeature) {
      return null;
    }

    if (selectedFeature?.geometry.type === 'Point') {
      const coordinates = selectedFeature.geometry.coordinates as [
        number,
        number,
      ];
      return {
        lat: coordinates[1],
        lon: coordinates[0],
        id: selectedFeature.id as string,
        name: selectedFeature.properties?.name,
        occurrences: selectedFeature.properties?.occurrences,
        serviceId: selectedFeature.properties?.serviceId,
      };
    }

    if (selectedFeature) {
      const center = turf_center(selectedFeature);

      return {
        lat: center.geometry.coordinates[1],
        lon: center.geometry.coordinates[0],
        id: selectedFeature.id as string,
        name: selectedFeature.properties?.name,
        occurrences: selectedFeature.properties?.occurrences,
      };
    }

    return null;
  },
);

export const getSelectedFeatureAsGeoJSON = createSelector(
  [
    (store: CoreAppStore): CoreAppStore['layers'] => store.layers,
    (_store: CoreAppStore, panelId: string): string => panelId,
  ],
  (layersState, panelId) =>
    resolveSelectedFeatureFromLayerSelection(layersState, panelId),
);

export const selectLinkedFeatureLayersForMap = createSelector(
  [
    selectFeatureLinksForAllPanels,
    (store: CoreAppStore): CoreAppStore['layers'] => store.layers,
    (_store: CoreAppStore, mapId: string): string => mapId,
  ],
  (linksByPanelId, layersState, mapId): FeatureLayerData[] => {
    if (!layersState) {
      return emptyFeatureLayers;
    }

    const linkedPanels = getLinkedFeatureSourcePanelIdsForMapId(
      linksByPanelId,
      mapId,
    );

    return linkedPanels.flatMap((panelId) =>
      getFeatureSyncLayersForPanel(layersState, panelId)
        .filter(({ mapId: layerMapId }) => layerMapId === mapId)
        .map(({ layerId }) => {
          const layer = layersState.byId[layerId];
          return toFeatureLayerData(layerId, layer);
        })
        .filter((layer): layer is FeatureLayerData => !!layer),
    );
  },
  selectorMemoizationOptions,
);

export const selectLinkedSharedDataForMap = createSelector(
  [
    selectFeatureLinksForAllPanels,
    synchronizationGroupStore,
    (_, mapId: string): string => mapId,
  ],
  (linksByPanelId, syncState, mapId): Record<string, PotentialData> => {
    const linkedPanels = getLinkedFeatureSourcePanelIdsForMapId(
      linksByPanelId,
      mapId,
    );

    const linkedSharedData: Record<string, PotentialData> = {};

    linkedPanels.forEach((panelId) => {
      const sharedData = getFeatureSyncStateByPanelId(syncState, panelId);
      if (sharedData) {
        linkedSharedData[panelId] = sharedData;
      }
    });

    return linkedSharedData;
  },
  selectorMemoizationOptions,
);

export const selectLinkedFormFeaturesForMap = createSelector(
  [
    selectFeatureLinksForAllPanels,
    synchronizationGroupStore,
    (_, mapId: string): string => mapId,
  ],
  (linksByPanelId, syncState, mapId): FormFeatureData[] => {
    const linkedPanels = getLinkedFeatureSourcePanelIdsForMapId(
      linksByPanelId,
      mapId,
    );

    return linkedPanels.flatMap(
      (panelId) =>
        getFeatureSyncStateByPanelId(syncState, panelId)?.formFeatures ?? [],
    );
  },
  selectorMemoizationOptions,
);

export const getSyncedStartCenterEndStepCurrentTime = createSelector(
  (coreAppStore, sourceId) =>
    getTime(getSynchronizationGroupStore(coreAppStore), sourceId),
  (store): TimeSliderStartEndStepCurrent | undefined => {
    if (!store?.timeSliderCenterTime || !store?.timeSliderSpan) {
      return undefined;
    }

    return {
      start: Math.round(store.timeSliderCenterTime - store.timeSliderSpan / 2),
      end: Math.round(store.timeSliderCenterTime + store.timeSliderSpan / 2),
      step: store?.timeSliderStep || 60,
      current: dateUtils.unixFromISOString(store?.currentTime),
    } as TimeSliderStartEndStepCurrent;
  },
  selectorMemoizationOptions,
);
